import { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../ui/button';

const businessNavLinks = [
  { name: 'The Mission', href: '/mission', type: 'route' },
  { name: 'Pricing', href: '/pricing', type: 'route' },
];

const customerNavLinks = [
  { name: 'How it works', href: '/customers#how-it-works', type: 'route' },
  { name: 'Trust', href: '/customers#trust', type: 'route' },
  { name: 'FAQ', href: '/customers#faq', type: 'route' },
];

/**
 * AudienceToggle — two labels with a maroon underline that slides
 * between them based on the active mode, and previews on hover.
 */
const AudienceToggle = ({ isCustomerMode, onNavigate, variant = 'desktop' }) => {
  const businessRef = useRef(null);
  const customersRef = useRef(null);
  const containerRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });
  const [hoverTarget, setHoverTarget] = useState(null); // 'business' | 'customers' | null

  const measure = (target) => {
    const el = target === 'customers' ? customersRef.current : businessRef.current;
    if (!el || !containerRef.current) return null;
    const elRect = el.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    return { left: elRect.left - containerRect.left, width: elRect.width };
  };

  // Position indicator under active label (or hovered label)
  useLayoutEffect(() => {
    const target = hoverTarget || (isCustomerMode ? 'customers' : 'business');
    const pos = measure(target);
    if (pos) setIndicator({ ...pos, ready: true });
  }, [isCustomerMode, hoverTarget]);

  // Re-measure on resize so the slide stays accurate
  useEffect(() => {
    const handle = () => {
      const target = hoverTarget || (isCustomerMode ? 'customers' : 'business');
      const pos = measure(target);
      if (pos) setIndicator((prev) => ({ ...pos, ready: prev.ready }));
    };
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, [isCustomerMode, hoverTarget]);

  const labelBase =
    'relative text-xs sm:text-sm font-semibold tracking-wide pb-1 focus:outline-none focus-visible:outline-none transition-colors duration-200';
  const activeColor = 'text-[#880808]';
  const inactiveColor = 'text-[#57534E] hover:text-[#880808]';

  const testIdSuffix = variant === 'mobile' ? '-mobile' : '';
  const containerTestId = variant === 'mobile' ? 'audience-toggle-mobile' : 'audience-toggle-desktop';

  return (
    <div
      ref={containerRef}
      className="relative flex items-center gap-5"
      data-testid={containerTestId}
    >
      <Link
        ref={businessRef}
        to="/"
        onClick={onNavigate}
        onMouseEnter={() => setHoverTarget('business')}
        onMouseLeave={() => setHoverTarget(null)}
        className={`${labelBase} ${!isCustomerMode ? activeColor : inactiveColor}`}
        data-testid={`audience-toggle-business${testIdSuffix}`}
      >
        For Businesses
      </Link>
      <span className="text-[#A8A29E] text-xs select-none" aria-hidden="true">·</span>
      <Link
        ref={customersRef}
        to="/customers"
        onClick={onNavigate}
        onMouseEnter={() => setHoverTarget('customers')}
        onMouseLeave={() => setHoverTarget(null)}
        className={`${labelBase} ${isCustomerMode ? activeColor : inactiveColor}`}
        data-testid={`audience-toggle-customers${testIdSuffix}`}
      >
        For Customers
      </Link>

      {/* Sliding underline */}
      <span
        aria-hidden="true"
        data-testid={`audience-toggle-indicator${testIdSuffix}`}
        className="absolute bottom-0 h-[2px] bg-[#880808] rounded-full pointer-events-none"
        style={{
          left: indicator.left,
          width: indicator.width,
          opacity: indicator.ready ? 1 : 0,
          transition:
            'left 320ms cubic-bezier(0.34, 1.2, 0.64, 1), width 320ms cubic-bezier(0.34, 1.2, 0.64, 1), opacity 200ms ease-out',
        }}
      />
    </div>
  );
};

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isCustomerMode = location.pathname.startsWith('/customers');

  const navLinks = isCustomerMode ? customerNavLinks : businessNavLinks;
  const ctaLabel = isCustomerMode ? 'Download' : 'Join free';
  const ctaHref = isCustomerMode ? '/customers/get' : '/get';
  const ctaIsAnchor = false;

  const renderNavLink = (link, isMobile = false) => {
    const isActive = link.type === 'route' && location.pathname === link.href;
    const desktopCls = `text-sm font-medium transition-colors duration-200 focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
      isActive ? 'text-[#880808]' : 'text-[#57534E] hover:text-[#880808]'
    }`;
    const mobileCls = `px-4 py-2 text-base font-medium rounded-lg transition-colors duration-200 ${
      isActive ? 'text-[#880808] bg-[#F5F5F4]' : 'text-[#57534E] hover:text-[#880808] hover:bg-[#F5F5F4]'
    }`;
    const cls = isMobile ? mobileCls : desktopCls;
    const testIdPrefix = isMobile ? 'mobile-nav-link' : 'nav-link';
    const testId = `${testIdPrefix}-${link.name.toLowerCase().replace(/\s+/g, '-')}`;

    if (link.type === 'anchor') {
      return (
        <a
          key={link.name}
          href={link.href}
          className={cls}
          onClick={() => isMobile && setMobileMenuOpen(false)}
          data-testid={testId}
        >
          {link.name}
        </a>
      );
    }
    return (
      <Link
        key={link.name}
        to={link.href}
        className={cls}
        onClick={() => isMobile && setMobileMenuOpen(false)}
        data-testid={testId}
      >
        {link.name}
      </Link>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAFAF9]/95 backdrop-blur-sm border-b border-[#E7E5E4]">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="navbar">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
            data-testid="navbar-logo"
          >
            <img src="/servitium-logo.png" alt="Servitium" className="h-8 md:h-9 w-auto" />
          </Link>

          {/* Desktop: Audience Toggle */}
          <div className="hidden md:flex">
            <AudienceToggle isCustomerMode={isCustomerMode} variant="desktop" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => renderNavLink(link, false))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center">
            {ctaIsAnchor ? (
              <a href={ctaHref} data-testid="navbar-cta">
                <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-6 py-2 text-sm font-semibold transition-all duration-200">
                  {ctaLabel}
                </Button>
              </a>
            ) : (
              <Link to={ctaHref} data-testid="navbar-cta">
                <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-6 py-2 text-sm font-semibold transition-all duration-200">
                  {ctaLabel}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="md:hidden p-2 rounded-lg text-[#57534E] hover:bg-[#F5F5F4]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#E7E5E4]" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              {/* Mobile: Audience Toggle (top) */}
              <div className="flex justify-center pb-3 border-b border-[#E7E5E4]">
                <AudienceToggle
                  isCustomerMode={isCustomerMode}
                  variant="mobile"
                  onNavigate={() => setMobileMenuOpen(false)}
                />
              </div>

              {navLinks.map((link) => renderNavLink(link, true))}

              <div className="px-4 pt-2">
                {ctaIsAnchor ? (
                  <a href={ctaHref} onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#880808] hover:bg-[#6b0606] text-white rounded-full py-3 text-sm font-semibold">
                      {ctaLabel}
                    </Button>
                  </a>
                ) : (
                  <Link to={ctaHref} onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-[#880808] hover:bg-[#6b0606] text-white rounded-full py-3 text-sm font-semibold">
                      {ctaLabel}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
